import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Close,
  CloudUpload,
} from "@material-ui/icons";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import PermissionService from "../../../services/PermissionService";
import { getUsers, getUserById } from "../../../store/hub/users";
import { getLoggedInUserId } from "../../../store/hub/loggedInUser";
import {
  getHrLeaveHolidays,
  getHrLeaveSicknesses,
  getHrLeaveOthers,
  getHrLeavePublicHolidays,
  getHrLeaveRefreshNonce,
  getHrLeaveRequestsRefreshNonce,
} from "../../../store/hub/hrLeave";
import {
  addSnackbarIssue,
} from "../../../components/common/StoreCommon";
import {
  apiAddHoliday,
  apiUpdateHoliday,
  apiDeleteHoliday,
  apiImportHolidays,
  getHrLeaveHolidays as fetchHrLeaveHolidays,
  getHrLeaveSicknesses as fetchHrLeaveSicknesses,
  getHrLeaveOthers as fetchHrLeaveOthers,
  getHrLeavePublicHolidays as fetchHrLeavePublicHolidays,
  apiAddSickness,
  apiUpdateSickness,
  apiDeleteSickness,
  apiImportSicknesses,
  apiAddOther,
  apiUpdateOther,
  apiDeleteOther,
  apiImportOthers,
} from "../../../components/common/ApiMiddleware";
import {
  localDateToUtcIso,
  parseDateInputValue,
  toDateInputValue,
  utcToLocalDate,
  buildWeekHolidaySegments,
  chunkIntoWeeks,
  defaultOtherDurationMins,
  formatDurationMins,
  parseDurationMins,
  hasConflictingLeave,
  isApprovedHoliday,
} from "../utils/holidayDates";

import HolidayListView from "./HolidayListView";
import HolidayDetailsView from "./HolidayDetailsView";
import HolidayMonthView from "./HolidayMonthView";
import HolidayRequestsView from "./HolidayRequestsView";

const OTHER_EVENT_TYPES = [
  "Overtime",
  "Time Off Unpaid",
  "Half Day (Paid) TOIL",
  "Unpaid Leave",
];

/*
**  Date values in this view are local (organisation/user locale), matching Stats DatePicker.
**  Native date inputs display in the browser locale; API traffic is UTC.
*/

export default function HrLeaveView({ viewMode = "calendar", detailsUser = null }) {
  const classes = useStyles();
  const permissionService = new PermissionService();
  const isHrAdmin = permissionService.hrAdminFl();
  const users = useSelector(getUsers)();
  const getUser = useSelector(getUserById);
  const loggedInUserId = useSelector(getLoggedInUserId)();
  const holidays = useSelector(getHrLeaveHolidays);
  const sicknesses = useSelector(getHrLeaveSicknesses);
  const others = useSelector(getHrLeaveOthers);
  const publicHolidays = useSelector(getHrLeavePublicHolidays);
  const refreshNonce = useSelector(getHrLeaveRefreshNonce);
  const requestsRefreshNonce = useSelector(getHrLeaveRequestsRefreshNonce);

  const sortedUsers = useMemo(() => {
    return [...(users || [])].sort((a, b) => {
      const nameA = `${a.FirstName || ""} ${a.LastName || ""}`.trim();
      const nameB = `${b.FirstName || ""} ${b.LastName || ""}`.trim();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const approvedHolidays = useMemo(
    () => (holidays || []).filter(isApprovedHoliday),
    [holidays]
  );

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [detailsYear, setDetailsYear] = useState(() => new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [durationType, setDurationType] = useState("WholeDay");
  const [partDayType, setPartDayType] = useState("AM");
  const [comments, setComments] = useState("");
  const [status, setStatus] = useState("Approved");
  const [actionedUserId, setActionedUserId] = useState(null);
  const [actionedDateTime, setActionedDateTime] = useState(null);
  const [eventType, setEventType] = useState("");
  const [durationText, setDurationText] = useState("8:00");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [recordType, setRecordType] = useState("holiday");
  const [importMenuAnchor, setImportMenuAnchor] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const holidayFileInputRef = useRef(null);
  const sicknessFileInputRef = useRef(null);
  const otherFileInputRef = useRef(null);

  const isEditing = editingHolidayId != null;
  const canEdit = isHrAdmin;
  const showTeamToolbar = viewMode === "calendar" || viewMode === "list";

  const visibleRange = useMemo(() => {
    if (viewMode === "year" || viewMode === "requests") {
      const fromLocal = new Date(detailsYear, 0, 1);
      const toLocal = new Date(detailsYear, 11, 31);
      return {
        fromLocal,
        toLocal,
        fromUtcIso: localDateToUtcIso(fromLocal),
        toUtcIso: new Date(
          toLocal.getFullYear(),
          toLocal.getMonth(),
          toLocal.getDate(),
          23,
          59,
          59,
          999
        ).toISOString(),
      };
    }

    // List/Month: calendar month so spanning leave that touches the month is included.
    // Calendar: pad to full weeks for the grid.
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const fromLocal =
      viewMode === "list" || viewMode === "month"
        ? monthStart
        : startOfWeek(monthStart, { weekStartsOn: 1 });
    const toLocal =
      viewMode === "list" || viewMode === "month"
        ? monthEnd
        : endOfWeek(monthEnd, { weekStartsOn: 1 });
    return {
      fromLocal,
      toLocal,
      fromUtcIso: localDateToUtcIso(fromLocal),
      toUtcIso: new Date(
        toLocal.getFullYear(),
        toLocal.getMonth(),
        toLocal.getDate(),
        23,
        59,
        59,
        999
      ).toISOString(),
    };
  }, [viewMode, detailsYear, month]);

  useEffect(() => {
    Promise.all([
      fetchHrLeaveHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso),
      fetchHrLeaveSicknesses(visibleRange.fromUtcIso, visibleRange.toUtcIso),
      fetchHrLeaveOthers(visibleRange.fromUtcIso, visibleRange.toUtcIso),
      fetchHrLeavePublicHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso),
    ]).catch(() => {
      addSnackbarIssue("Unable to load leave data", "warning");
    });
  }, [visibleRange.fromUtcIso, visibleRange.toUtcIso, refreshNonce]);

  useEffect(() => {
    if (viewMode !== "requests" || requestsRefreshNonce === 0) return;
    fetchHrLeaveHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso).catch(
      () => {
        addSnackbarIssue("Unable to load leave data", "warning");
      }
    );
  }, [requestsRefreshNonce]);

  const calendarDays = useMemo(() => {
    return eachDayOfInterval({
      start: visibleRange.fromLocal,
      end: visibleRange.toLocal,
    });
  }, [visibleRange]);

  const weeks = useMemo(() => chunkIntoWeeks(calendarDays), [calendarDays]);

  const getHolidayLabel = (holiday) => {
    if (holiday.recordType === "publicHoliday") {
      return holiday.title || "Public Holiday";
    }
    const user = getUser(holiday.appUserId);
    const label = user
      ? `${user.FirstName} ${user.LastName}`
      : `User ${holiday.appUserId}`;
    if (holiday.recordType === "other") {
      return holiday.eventType ? `${label} (${holiday.eventType})` : label;
    }
    const skipSuffix =
      holiday.recordType === "sickness" || !holiday.partDayType;
    const suffix =
      !skipSuffix && holiday.durationType === "PartDay"
        ? ` (${holiday.partDayType})`
        : "";
    return `${label}${suffix}`;
  };

  const applyDefaultOtherDuration = (start, end) => {
    const mins = defaultOtherDurationMins(start, end);
    setDurationText(formatDurationMins(mins));
  };

  const resetDialogForm = (keepRecordType = false) => {
    setEditingHolidayId(null);
    setSelectedUser(null);
    setStartDate(null);
    setEndDate(null);
    setDurationType("WholeDay");
    setPartDayType("AM");
    setComments("");
    setStatus("Approved");
    setActionedUserId(null);
    setActionedDateTime(null);
    setEventType("");
    setDurationText("8:00");
    setFormError("");
    if (!keepRecordType) {
      setRecordType("holiday");
    }
  };

  const openAddDialog = (type = "holiday") => {
    setRecordType(type);
    resetDialogForm(true);
    if (type === "other") {
      setDurationText("8:00");
    }
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    let type = "holiday";
    if (record.recordType === "other" || record.durationMins != null) {
      type = "other";
    } else if (record.recordType === "sickness" || !record.durationType) {
      type = "sickness";
    }
    setRecordType(type);
    setEditingHolidayId(record.id);
    setSelectedUser(getUser(record.appUserId) || null);
    setStartDate(utcToLocalDate(record.startDateTime));
    setEndDate(utcToLocalDate(record.endDateTime));
    if (type === "holiday") {
      setDurationType(record.durationType || "WholeDay");
      setPartDayType(record.partDayType || "AM");
      setComments(record.comments || "");
      setStatus(record.status || "Approved");
      setActionedUserId(record.actionedUserId || null);
      setActionedDateTime(record.actionedDateTime || null);
      setEventType("");
      setDurationText("8:00");
    } else if (type === "other") {
      setDurationType("WholeDay");
      setPartDayType("AM");
      setComments(record.comments || "");
      setStatus("Approved");
      setActionedUserId(null);
      setActionedDateTime(null);
      setEventType(record.eventType || "");
      setDurationText(formatDurationMins(record.durationMins || 0));
    } else {
      setDurationType("WholeDay");
      setPartDayType("AM");
      setComments("");
      setStatus("Approved");
      setActionedUserId(null);
      setActionedDateTime(null);
      setEventType("");
      setDurationText("8:00");
    }
    setFormError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetDialogForm();
  };

  const validateForm = () => {
    if (!selectedUser) {
      setFormError("Please select a user");
      return false;
    }
    if (!startDate) {
      setFormError("Please enter a start date");
      return false;
    }
    if (!endDate) {
      setFormError("Please enter an end date");
      return false;
    }
    if (endDate < startDate) {
      setFormError("End date must be on or after the start date");
      return false;
    }
    if (recordType === "other") {
      if (!eventType.trim()) {
        setFormError("Please enter a type");
        return false;
      }
      if (parseDurationMins(durationText) == null) {
        setFormError("Please enter duration as H:MM (e.g. 2:00)");
        return false;
      }
    }
    if (
      recordType === "holiday" &&
      durationType === "PartDay" &&
      toDateInputValue(startDate) !== toDateInputValue(endDate)
    ) {
      setFormError("Part-day holidays must start and end on the same day");
      return false;
    }
    if (
      hasConflictingLeave({
        appUserId: selectedUser.Id,
        rangeStart: startDate,
        rangeEnd: endDate,
        holidays,
        sicknesses,
        others,
        excludeHolidayId:
          isEditing && recordType === "holiday" ? editingHolidayId : null,
        excludeSicknessId:
          isEditing && recordType === "sickness" ? editingHolidayId : null,
        excludeOtherId:
          isEditing && recordType === "other" ? editingHolidayId : null,
      })
    ) {
      setFormError(
        "This user already has a holiday, sickness, or other event on one or more of these days"
      );
      return false;
    }
    return true;
  };

  const refreshLeaveData = () =>
    Promise.all([
      fetchHrLeaveHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso),
      fetchHrLeaveSicknesses(visibleRange.fromUtcIso, visibleRange.toUtcIso),
      fetchHrLeaveOthers(visibleRange.fromUtcIso, visibleRange.toUtcIso),
    ]);

  const handleSave = async () => {
    if (!canEdit || !validateForm()) return;

    setFormError("");
    setSaving(true);
    try {
      if (recordType === "sickness") {
        const payload = {
          appUserId: selectedUser.Id,
          startDateTime: localDateToUtcIso(startDate),
          endDateTime: localDateToUtcIso(endDate),
        };

        if (isEditing) {
          await apiUpdateSickness({ sicknessId: editingHolidayId, ...payload });
          addSnackbarIssue("Sickness updated", "success", false, 5000);
        } else {
          await apiAddSickness(payload);
          addSnackbarIssue("Sickness added", "success", false, 5000);
        }
      } else if (recordType === "other") {
        const payload = {
          appUserId: selectedUser.Id,
          startDateTime: localDateToUtcIso(startDate),
          endDateTime: localDateToUtcIso(endDate),
          eventType: eventType.trim(),
          durationMins: parseDurationMins(durationText),
          comments: comments || null,
        };

        if (isEditing) {
          await apiUpdateOther({ otherId: editingHolidayId, ...payload });
          addSnackbarIssue("Other leave updated", "success", false, 5000);
        } else {
          await apiAddOther(payload);
          addSnackbarIssue("Other leave added", "success", false, 5000);
        }
      } else {
        const payload = {
          appUserId: selectedUser.Id,
          startDateTime: localDateToUtcIso(startDate),
          endDateTime: localDateToUtcIso(endDate),
          durationType,
          partDayType: durationType === "PartDay" ? partDayType : null,
          comments: comments || null,
        };

        if (isEditing) {
          await apiUpdateHoliday({ holidayId: editingHolidayId, ...payload });
          addSnackbarIssue("Holiday updated", "success", false, 5000);
        } else {
          await apiAddHoliday(payload);
          addSnackbarIssue("Holiday added", "success", false, 5000);
        }
      }

      closeDialog();
      await refreshLeaveData();
    } catch (e) {
      const noun =
        recordType === "sickness"
          ? "sickness"
          : recordType === "other"
            ? "other leave"
            : "holiday";
      setFormError(
        isEditing
          ? `Unable to update ${noun}. Please check the details and try again.`
          : `Unable to add ${noun}. Please check the details and try again.`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromDialog = async () => {
    if (!canEdit || !isEditing) return;
    const noun =
      recordType === "sickness"
        ? "sickness"
        : recordType === "other"
          ? "other leave"
          : "holiday";
    if (!window.confirm(`Delete this ${noun} record?`)) return;

    setSaving(true);
    try {
      if (recordType === "sickness") {
        await apiDeleteSickness(editingHolidayId);
        addSnackbarIssue("Sickness removed", "success", false, 5000);
      } else if (recordType === "other") {
        await apiDeleteOther(editingHolidayId);
        addSnackbarIssue("Other leave removed", "success", false, 5000);
      } else {
        await apiDeleteHoliday(editingHolidayId);
        addSnackbarIssue("Holiday removed", "success", false, 5000);
      }
      closeDialog();
    } catch (e) {
      setFormError(`Unable to delete ${noun}`);
    } finally {
      setSaving(false);
    }
  };

  const handleImportHoliday = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const result = await apiImportHolidays(file);
      await fetchHrLeaveHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso);
      const unmatched = (result.UnmatchedNames || []).length;
      addSnackbarIssue(
        `Imported ${result.ImportedCount || 0}, skipped ${result.SkippedCount || 0}` +
          (unmatched ? ` (${unmatched} unmatched names)` : ""),
        "success",
        false,
        5000
      );
    } catch (e) {
      addSnackbarIssue("Unable to import holidays", "warning");
    }
  };

  const handleImportSickness = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const result = await apiImportSicknesses(file);
      await fetchHrLeaveSicknesses(visibleRange.fromUtcIso, visibleRange.toUtcIso);
      const unmatched = (result.UnmatchedNames || []).length;
      addSnackbarIssue(
        `Imported ${result.ImportedCount || 0}, skipped ${result.SkippedCount || 0}` +
          (unmatched ? ` (${unmatched} unmatched names)` : ""),
        "success",
        false,
        5000
      );
    } catch (e) {
      addSnackbarIssue("Unable to import sicknesses", "warning");
    }
  };

  const handleImportOther = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const result = await apiImportOthers(file);
      await fetchHrLeaveOthers(visibleRange.fromUtcIso, visibleRange.toUtcIso);
      const unmatched = (result.UnmatchedNames || []).length;
      addSnackbarIssue(
        `Imported ${result.ImportedCount || 0}, skipped ${result.SkippedCount || 0}` +
          (unmatched ? ` (${unmatched} unmatched names)` : ""),
        "success",
        false,
        5000
      );
    } catch (e) {
      addSnackbarIssue("Unable to import other leave", "warning");
    }
  };

  const dateInputStyle = {
    width: "100%",
    height: 40,
    padding: "8px 12px",
    fontSize: 14,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <Box className={classes.root}>
      {showTeamToolbar ? (
      <Box className={classes.toolbar}>
        <Box className={classes.monthNav}>
          <IconButton size="small" onClick={() => setMonth((m) => addMonths(m, -1))}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" className={classes.monthTitle}>
            {format(month, "MMMM yyyy")}
          </Typography>
          <IconButton size="small" onClick={() => setMonth((m) => addMonths(m, 1))}>
            <ChevronRight />
          </IconButton>
        </Box>
        {isHrAdmin ? (
          <Box className={classes.actions}>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={(e) => setImportMenuAnchor(e.currentTarget)}
            >
              Import
            </Button>
            <Menu
              anchorEl={importMenuAnchor}
              open={Boolean(importMenuAnchor)}
              onClose={() => setImportMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setImportMenuAnchor(null);
                  holidayFileInputRef.current?.click();
                }}
              >
                Holidays
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setImportMenuAnchor(null);
                  sicknessFileInputRef.current?.click();
                }}
              >
                Sickness
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setImportMenuAnchor(null);
                  otherFileInputRef.current?.click();
                }}
              >
                Other
              </MenuItem>
            </Menu>
            <input
              ref={holidayFileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleImportHoliday}
            />
            <input
              ref={sicknessFileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleImportSickness}
            />
            <input
              ref={otherFileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleImportOther}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            >
              Add
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  openAddDialog("holiday");
                }}
              >
                Holiday
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  openAddDialog("sickness");
                }}
              >
                Sickness
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null);
                  openAddDialog("other");
                }}
              >
                Other
              </MenuItem>
            </Menu>
          </Box>
        ) : null}
      </Box>
      ) : null}

      {viewMode === "year" ? (
        <HolidayDetailsView
          detailsUser={detailsUser}
          holidays={approvedHolidays}
          sicknesses={sicknesses}
          year={detailsYear}
          onYearChange={setDetailsYear}
        />
      ) : null}

      {viewMode === "requests" ? (
        <HolidayRequestsView
          holidays={holidays}
          year={detailsYear}
          onYearChange={setDetailsYear}
          isHrAdmin={isHrAdmin}
          getUser={getUser}
          loggedInUserId={loggedInUserId}
          onRequested={() =>
            fetchHrLeaveHolidays(visibleRange.fromUtcIso, visibleRange.toUtcIso)
          }
        />
      ) : null}

      {viewMode === "month" ? (
        <HolidayMonthView
          detailsUser={detailsUser}
          holidays={approvedHolidays}
          sicknesses={sicknesses}
          others={others}
          publicHolidays={publicHolidays}
          month={month}
          onMonthChange={setMonth}
        />
      ) : null}

      {viewMode === "calendar" ? (
        <Box className={classes.weekHeader}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <Typography key={d} variant="caption" className={classes.weekHeaderCell}>
              {d}
            </Typography>
          ))}
        </Box>
      ) : null}

      {viewMode === "calendar" ? (
      <Box className={classes.weeks}>
        {weeks.map((weekDays) => {
          const calendarRecords = [
            ...publicHolidays.map((p) => ({ ...p, recordType: "publicHoliday" })),
            ...approvedHolidays.map((h) => ({ ...h, recordType: "holiday" })),
            ...sicknesses.map((s) => ({ ...s, recordType: "sickness" })),
            ...others.map((o) => ({ ...o, recordType: "other" })),
          ];
          const segments = buildWeekHolidaySegments(weekDays, calendarRecords);
          const laneCount = Math.max(
            1,
            ...segments.map((segment) => segment.lane + 1),
            0
          );
          const weekKey = toDateInputValue(weekDays[0]);

          return (
            <Box key={weekKey} className={classes.weekRow}>
              <Box className={classes.weekDays}>
                {weekDays.map((day) => {
                  const inMonth = isSameMonth(day, month);
                  return (
                    <Box
                      key={toDateInputValue(day)}
                      className={`${classes.dayCell} ${inMonth ? "" : classes.dayOutside}`}
                    >
                      <Typography variant="body2" className={classes.dayNumber}>
                        {format(day, "d")}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box
                className={classes.weekEvents}
                style={{
                  gridTemplateRows: `repeat(${laneCount}, 26px)`,
                }}
              >
                {segments.map((segment) => {
                  const label = getHolidayLabel(segment.holiday);
                  const radiusLeft = segment.continuesBefore ? 0 : 12;
                  const radiusRight = segment.continuesAfter ? 0 : 12;
                  const isSickness = segment.holiday.recordType === "sickness";
                  const isOther = segment.holiday.recordType === "other";
                  const isPublicHoliday =
                    segment.holiday.recordType === "publicHoliday";
                  const eventClass = isPublicHoliday
                    ? classes.eventBarPublicHoliday
                    : isSickness
                      ? classes.eventBarSickness
                      : isOther
                        ? classes.eventBarOther
                        : classes.eventBar;
                  const clickable = !isPublicHoliday;
                  return (
                    <Box
                      key={`${segment.holiday.recordType}-${segment.holiday.id}-${segment.startCol}-${segment.endCol}`}
                      className={`${eventClass} ${clickable ? classes.eventBarClickable : ""}`}
                      title={label}
                      onClick={
                        clickable
                          ? () => openEditDialog(segment.holiday)
                          : undefined
                      }
                      style={{
                        gridColumn: `${segment.startCol + 1} / ${segment.endCol + 2}`,
                        gridRow: segment.lane + 1,
                        borderRadius: `${radiusLeft}px ${radiusRight}px ${radiusRight}px ${radiusLeft}px`,
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
      ) : null}

      {viewMode === "list" ? (
        <HolidayListView
          holidays={approvedHolidays}
          sicknesses={sicknesses}
          others={others}
          publicHolidays={publicHolidays}
          month={month}
          getUser={getUser}
          isHrAdmin={isHrAdmin}
          onSelectHoliday={openEditDialog}
        />
      ) : null}

      <Modal open={dialogOpen} onClose={closeDialog} className={classes.modal}>
        <Fade in={dialogOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6">
                {(() => {
                  const noun =
                    recordType === "sickness"
                      ? "Sickness"
                      : recordType === "other"
                        ? "Other"
                        : "Holiday";
                  if (isEditing) {
                    return canEdit ? `Edit ${noun.toLowerCase()}` : `${noun} details`;
                  }
                  return `Add ${noun.toLowerCase()}`;
                })()}
              </Typography>
              <IconButton size="small" onClick={closeDialog}>
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalBody}>
              <Autocomplete
                options={sortedUsers}
                getOptionLabel={(u) => `${u.FirstName} ${u.LastName}`}
                value={selectedUser}
                disabled={!canEdit}
                onChange={(_, value) => {
                  setSelectedUser(value);
                  setFormError("");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="User" variant="outlined" fullWidth />
                )}
              />
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  Start date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(startDate)}
                  disabled={!canEdit}
                  onChange={(e) => {
                    const next = parseDateInputValue(e.target.value);
                    setStartDate(next);
                    const nextEnd = endDate || next;
                    if (next && !endDate) setEndDate(next);
                    if (recordType === "other" && next && nextEnd) {
                      applyDefaultOtherDuration(next, nextEnd);
                    }
                    setFormError("");
                  }}
                  style={dateInputStyle}
                />
              </Box>
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  End date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(endDate)}
                  min={toDateInputValue(startDate) || undefined}
                  disabled={!canEdit}
                  onChange={(e) => {
                    const next = parseDateInputValue(e.target.value);
                    setEndDate(next);
                    if (recordType === "other" && startDate && next) {
                      applyDefaultOtherDuration(startDate, next);
                    }
                    setFormError("");
                  }}
                  style={dateInputStyle}
                />
              </Box>
              {recordType === "holiday" ? (
                <>
                  <FormControl variant="outlined" fullWidth disabled={!canEdit}>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      label="Duration"
                      value={durationType}
                      onChange={(e) => {
                        setDurationType(e.target.value);
                        setFormError("");
                      }}
                    >
                      <MenuItem value="WholeDay">Whole day</MenuItem>
                      <MenuItem value="PartDay">Part day</MenuItem>
                    </Select>
                  </FormControl>
                  {durationType === "PartDay" ? (
                    <FormControl variant="outlined" fullWidth disabled={!canEdit}>
                      <InputLabel>Part of day</InputLabel>
                      <Select
                        label="Part of day"
                        value={partDayType}
                        onChange={(e) => {
                          setPartDayType(e.target.value);
                          setFormError("");
                        }}
                      >
                        <MenuItem value="AM">AM</MenuItem>
                        <MenuItem value="PM">PM</MenuItem>
                      </Select>
                    </FormControl>
                  ) : null}
                  <TextField
                    label="Comments"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    value={comments}
                    disabled={!canEdit}
                    onChange={(e) => setComments(e.target.value)}
                  />
                  {isEditing ? (
                    <>
                      <TextField
                        label="Status"
                        variant="outlined"
                        fullWidth
                        value={status || "Approved"}
                        disabled
                      />
                      <TextField
                        label="Actioned By"
                        variant="outlined"
                        fullWidth
                        value={(() => {
                          if (!actionedUserId) return "";
                          const actionedUser = getUser(actionedUserId);
                          return actionedUser
                            ? `${actionedUser.FirstName} ${actionedUser.LastName}`
                            : `User ${actionedUserId}`;
                        })()}
                        disabled
                      />
                      <TextField
                        label="Actioned On"
                        variant="outlined"
                        fullWidth
                        value={
                          actionedDateTime
                            ? (() => {
                                const date = new Date(actionedDateTime);
                                return Number.isNaN(date.getTime())
                                  ? ""
                                  : date.toLocaleString();
                              })()
                            : ""
                        }
                        disabled
                      />
                    </>
                  ) : null}
                </>
              ) : null}
              {recordType === "other" ? (
                <>
                  <Autocomplete
                    freeSolo
                    options={OTHER_EVENT_TYPES}
                    value={eventType}
                    disabled={!canEdit}
                    onChange={(_, value) => {
                      setEventType(value || "");
                      setFormError("");
                    }}
                    onInputChange={(_, value) => {
                      setEventType(value || "");
                      setFormError("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Type"
                        variant="outlined"
                        fullWidth
                        placeholder="e.g. Overtime"
                      />
                    )}
                  />
                  <TextField
                    label="Duration (H:MM)"
                    variant="outlined"
                    fullWidth
                    value={durationText}
                    disabled={!canEdit}
                    onChange={(e) => {
                      setDurationText(e.target.value);
                      setFormError("");
                    }}
                    helperText="Defaults to days × 8 hours when dates change"
                  />
                  <TextField
                    label="Comments"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    value={comments}
                    disabled={!canEdit}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </>
              ) : null}
              {formError ? (
                <Typography variant="caption" className={classes.errorMessage}>
                  {formError}
                </Typography>
              ) : null}
            </Box>
            <Box className={classes.modalActions}>
              {isEditing && canEdit ? (
                <Button
                  color="secondary"
                  disabled={saving}
                  onClick={handleDeleteFromDialog}
                  className={`${classes.deleteButton} ${classes.dialogActionButton}`}
                >
                  Delete
                </Button>
              ) : null}
              <Box className={classes.modalActionsRight}>
                <Button
                  onClick={closeDialog}
                  className={classes.dialogActionButton}
                >
                  {canEdit ? "Cancel" : "Close"}
                </Button>
                {canEdit ? (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    onClick={handleSave}
                    className={classes.dialogActionButton}
                  >
                    Save
                  </Button>
                ) : null}
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "calc(100vh - 64px)",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  monthTitle: {
    minWidth: 180,
    textAlign: "center",
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  weekHeader: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 1,
    marginBottom: theme.spacing(0.5),
  },
  weekHeaderCell: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  weeks: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.divider,
    gap: 1,
  },
  weekRow: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minHeight: 110,
    backgroundColor: theme.palette.background.paper,
    position: "relative",
  },
  weekDays: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 1,
    backgroundColor: theme.palette.divider,
    flex: "1 1 auto",
    minHeight: 110,
  },
  dayCell: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.75),
    minHeight: 110,
    overflow: "hidden",
  },
  dayOutside: {
    backgroundColor: theme.palette.action.hover,
    opacity: 0.65,
  },
  dayNumber: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  weekEvents: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 32,
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    columnGap: 1,
    rowGap: 4,
    padding: "0 2px",
    pointerEvents: "none",
  },
  eventBar: {
    backgroundColor: "#1e88e5",
    color: "#fff",
    padding: "4px 8px",
    fontSize: 13,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
    pointerEvents: "auto",
    margin: "0 1px",
    boxSizing: "border-box",
    alignSelf: "center",
    minHeight: 22,
  },
  eventBarSickness: {
    backgroundColor: "#c2185b",
    color: "#fff",
    padding: "4px 8px",
    fontSize: 13,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
    pointerEvents: "auto",
    margin: "0 1px",
    boxSizing: "border-box",
    alignSelf: "center",
    minHeight: 22,
  },
  eventBarOther: {
    backgroundColor: "#ef6c00",
    color: "#fff",
    padding: "4px 8px",
    fontSize: 13,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
    pointerEvents: "auto",
    margin: "0 1px",
    boxSizing: "border-box",
    alignSelf: "center",
    minHeight: 22,
  },
  eventBarPublicHoliday: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    padding: "4px 8px",
    fontSize: 13,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
    pointerEvents: "auto",
    margin: "0 1px",
    boxSizing: "border-box",
    alignSelf: "center",
    minHeight: 22,
  },
  eventBarClickable: {
    cursor: "pointer",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    width: 440,
    maxWidth: "95vw",
    outline: "none",
    padding: theme.spacing(2),
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  dateLabel: {
    display: "block",
    marginBottom: 4,
    color: theme.palette.text.secondary,
  },
  modalActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  modalActionsRight: {
    display: "flex",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  deleteButton: {
    marginRight: "auto",
  },
  dialogActionButton: {
    fontWeight: 700,
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
}));
